import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, BarChart3, Calendar, MessageCircle, FileText, Activity, ArrowLeft, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportsPage({ user }) {
  const [areas, setAreas] = useState(["All Areas"]);
  const [summary, setSummary] = useState(null);
  const [engagement, setEngagement] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedArea, setSelectedArea] = useState('All Areas');
  const [sortField, setSortField] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedEvent, setExpandedEvent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/areas`).then(res => setAreas(["All Areas", ...res.data])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const areaParam = selectedArea !== 'All Areas' ? `?area=${encodeURIComponent(selectedArea)}` : '';
      const [summaryRes, engagementRes, meetingsRes] = await Promise.all([
        axios.get(`${API}/reports/summary${areaParam}`),
        axios.get(`${API}/reports/engagement${areaParam}`),
        axios.get(`${API}/reports/meeting-attendance${areaParam}`)
      ]);
      setSummary(summaryRes.data);
      setEngagement(engagementRes.data);
      setMeetings(meetingsRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const sortedEngagement = [...engagement].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDir === 'asc' 
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Area', 'Business', 'Logins', 'Posts', 'Comments', 'Messages', 'Events Attended', 'Last Login'];
    const rows = sortedEngagement.map(m => [
      m.full_name, m.email, m.phone, m.area, m.business_name,
      m.login_count, m.posts_count, m.comments_count, m.messages_sent, m.events_attended,
      m.last_login ? format(new Date(m.last_login), 'yyyy-MM-dd HH:mm') : 'Never'
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `engagement_report_${selectedArea.replace(/\s/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (loading) return <div className="text-center py-12 text-[#64748b]">Loading reports...</div>;

  return (
    <div className="max-w-7xl mx-auto px-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link to={user.is_admin ? '/admin' : '/area-manager'}>
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-[#2d3748]">Reports & Analytics</h1>
        </div>
        <div className="flex items-center gap-3">
          {user.is_admin && (
            <Select value={selectedArea} onValueChange={setSelectedArea}>
              <SelectTrigger className="w-[220px] rounded-full" data-testid="area-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {areas.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={exportCSV} className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-5 pb-4 text-center">
              <Users className="w-6 h-6 text-[#5b7f95] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#2d3748]">{summary.total_members}</p>
              <p className="text-xs text-[#64748b]">Total Members</p>
            </CardContent>
          </Card>
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-5 pb-4 text-center">
              <Activity className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{summary.active_members}</p>
              <p className="text-xs text-[#64748b]">Active Members</p>
            </CardContent>
          </Card>
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-5 pb-4 text-center">
              <Users className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-500">{summary.inactive_members}</p>
              <p className="text-xs text-[#64748b]">Never Logged In</p>
            </CardContent>
          </Card>
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-5 pb-4 text-center">
              <FileText className="w-6 h-6 text-[#cb9944] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#cb9944]">{summary.total_posts}</p>
              <p className="text-xs text-[#64748b]">Posts</p>
            </CardContent>
          </Card>
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-5 pb-4 text-center">
              <MessageCircle className="w-6 h-6 text-[#5b7f95] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#5b7f95]">{summary.total_messages}</p>
              <p className="text-xs text-[#64748b]">Messages</p>
            </CardContent>
          </Card>
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardContent className="pt-5 pb-4 text-center">
              <Calendar className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{summary.total_bookings}</p>
              <p className="text-xs text-[#64748b]">Event Bookings</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'overview' ? 'default' : 'outline'}
          onClick={() => setActiveTab('overview')}
          className={`rounded-full ${activeTab === 'overview' ? 'bg-[#5b7f95]' : ''}`}
          data-testid="tab-overview"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Member Engagement
        </Button>
        <Button
          variant={activeTab === 'meetings' ? 'default' : 'outline'}
          onClick={() => setActiveTab('meetings')}
          className={`rounded-full ${activeTab === 'meetings' ? 'bg-[#5b7f95]' : ''}`}
          data-testid="tab-meetings"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Meeting Attendance
        </Button>
      </div>

      {/* Member Engagement Tab */}
      {activeTab === 'overview' && (
        <Card className="border-gray-100 rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-[#5b7f95]" />
              Member Engagement ({sortedEngagement.length} members)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="engagement-table">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-2 font-medium text-[#64748b]">
                      <button onClick={() => handleSort('full_name')} className="flex items-center gap-1 hover:text-[#2d3748]">
                        Member <SortIcon field="full_name" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-[#64748b]">
                      <button onClick={() => handleSort('area')} className="flex items-center gap-1 hover:text-[#2d3748]">
                        Chapter <SortIcon field="area" />
                      </button>
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-[#64748b]">
                      <button onClick={() => handleSort('login_count')} className="flex items-center gap-1 hover:text-[#2d3748]">
                        Logins <SortIcon field="login_count" />
                      </button>
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-[#64748b]">
                      <button onClick={() => handleSort('posts_count')} className="flex items-center gap-1 hover:text-[#2d3748]">
                        Posts <SortIcon field="posts_count" />
                      </button>
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-[#64748b]">
                      <button onClick={() => handleSort('comments_count')} className="flex items-center gap-1 hover:text-[#2d3748]">
                        Comments <SortIcon field="comments_count" />
                      </button>
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-[#64748b]">
                      <button onClick={() => handleSort('messages_sent')} className="flex items-center gap-1 hover:text-[#2d3748]">
                        Messages <SortIcon field="messages_sent" />
                      </button>
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-[#64748b]">
                      <button onClick={() => handleSort('events_attended')} className="flex items-center gap-1 hover:text-[#2d3748]">
                        Meetings <SortIcon field="events_attended" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-[#64748b]">
                      <button onClick={() => handleSort('last_login')} className="flex items-center gap-1 hover:text-[#2d3748]">
                        Last Active <SortIcon field="last_login" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEngagement.map((member) => (
                    <tr key={member.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid="engagement-row">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.profile_image ? getImageUrl(member.profile_image) : undefined} />
                            <AvatarFallback className="bg-[#5b7f95] text-white text-xs">
                              {member.full_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-[#2d3748]">{member.full_name}</p>
                            {member.business_name && (
                              <p className="text-xs text-[#64748b]">{member.business_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-[#64748b]">{member.area}</td>
                      <td className="py-3 px-2 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium ${member.login_count > 0 ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-400'}`}>
                          {member.login_count || 0}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium ${member.posts_count > 0 ? 'bg-[#cb9944]/10 text-[#cb9944]' : 'bg-gray-100 text-gray-400'}`}>
                          {member.posts_count}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium ${member.comments_count > 0 ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                          {member.comments_count}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium ${member.messages_sent > 0 ? 'bg-[#5b7f95]/10 text-[#5b7f95]' : 'bg-gray-100 text-gray-400'}`}>
                          {member.messages_sent}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium ${member.events_attended > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                          {member.events_attended}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-[#64748b] text-xs">
                        {member.last_login 
                          ? format(new Date(member.last_login), 'dd MMM yyyy, HH:mm')
                          : <span className="text-red-400">Never</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Meeting Attendance Tab */}
      {activeTab === 'meetings' && (
        <div className="space-y-4">
          {meetings.length > 0 ? meetings.map((event) => (
            <Card key={event.id} className="border-gray-100 rounded-2xl shadow-sm">
              <CardContent className="pt-6">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                  data-testid="meeting-card"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#5b7f95]/10 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-[#5b7f95]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2d3748]">{event.title}</h3>
                      <p className="text-sm text-[#64748b]">
                        {format(new Date(event.date), 'PPP p')} &bull; {event.location}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                      {event.attendee_details?.length || 0}/{event.max_attendees || '∞'} attending
                    </span>
                    {expandedEvent === event.id ? <ChevronUp className="w-5 h-5 text-[#64748b]" /> : <ChevronDown className="w-5 h-5 text-[#64748b]" />}
                  </div>
                </div>
                
                {expandedEvent === event.id && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    {event.attendee_details?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {event.attendee_details.map((attendee) => (
                          <div key={attendee.id} className="flex items-center gap-3 p-3 bg-[#f8fafc] rounded-xl">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={attendee.profile_image ? getImageUrl(attendee.profile_image) : undefined} />
                              <AvatarFallback className="bg-[#5b7f95] text-white text-sm">
                                {(attendee.display_name || attendee.full_name).charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-[#2d3748] truncate">{attendee.display_name || attendee.full_name}</p>
                              <p className="text-xs text-[#64748b] truncate">{attendee.email}</p>
                              {attendee.phone && (
                                <p className="text-xs text-[#64748b]">{attendee.phone}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-[#64748b] py-4">No attendees yet</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )) : (
            <Card className="border-gray-100 rounded-2xl shadow-sm">
              <CardContent className="py-12 text-center text-[#64748b]">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No meetings found</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
